import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { crmConfig, crmWebhookHeaders } from "@/lib/crmConfig";
import type {
  CrmCanal,
  CrmConversa,
  CrmMensagem,
  CrmCampanha,
  CrmCampanhaEnvio,
  CrmEstagio,
  CrmCampanhaFiltros,
} from "@/types/crm";
import {
  CRM_DEMO_VISUAL,
  MOCK_CAMPANHAS,
  MOCK_CANAIS,
  MOCK_CONVERSAS,
  MOCK_MENSAGENS,
  filterMockConversas,
  getMockMensagens,
} from "@/components/crm/crmMockData";

// As tabelas crm_* ainda não estão nos tipos gerados do Supabase.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = () => supabase as any;

// ---------------------------------------------------------------------------
// Canais
// ---------------------------------------------------------------------------
export function useCrmCanais() {
  return useQuery({
    queryKey: ["crm", "canais", CRM_DEMO_VISUAL ? "demo" : "live"],
    queryFn: async (): Promise<CrmCanal[]> => {
      if (CRM_DEMO_VISUAL) return MOCK_CANAIS;
      const { data, error } = await db()
        .from("crm_canais")
        .select("*")
        .order("tipo");
      if (error) throw error;
      return (data ?? []) as CrmCanal[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Conversas (inbox / kanban)
// ---------------------------------------------------------------------------
export interface ConversasFiltro {
  estagio?: CrmEstagio | null;
  busca?: string | null;
  apenasMinhas?: boolean;
}

export function useCrmConversas(filtro?: ConversasFiltro) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      "crm",
      "conversas",
      CRM_DEMO_VISUAL ? "demo" : "live",
      filtro ?? null,
      filtro?.apenasMinhas ? user?.id : null,
    ],
    queryFn: async (): Promise<CrmConversa[]> => {
      if (CRM_DEMO_VISUAL) {
        return filterMockConversas(filtro, user?.id);
      }
      let q = db()
        .from("crm_conversas")
        .select("*")
        .order("ultima_msg_em", { ascending: false });

      if (filtro?.estagio) q = q.eq("estagio", filtro.estagio);
      if (filtro?.apenasMinhas && user?.id) q = q.eq("responsavel_id", user.id);
      if (filtro?.busca) {
        const term = `%${filtro.busca}%`;
        q = q.or(`nome_contato.ilike.${term},telefone_cliente.ilike.${term}`);
      }

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as CrmConversa[];
    },
    refetchInterval: CRM_DEMO_VISUAL ? false : 20000,
  });

  // Realtime: atualiza a lista quando há mudança em crm_conversas
  useEffect(() => {
    if (CRM_DEMO_VISUAL) return;
    const channel = supabase
      .channel(`crm_conversas_changes_${crypto.randomUUID()}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "crm_conversas" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["crm", "conversas"] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return query;
}

// ---------------------------------------------------------------------------
// Mensagens de uma conversa
// ---------------------------------------------------------------------------
export function useCrmMensagens(conversaId?: string | null) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["crm", "mensagens", CRM_DEMO_VISUAL ? "demo" : "live", conversaId],
    enabled: !!conversaId,
    queryFn: async (): Promise<CrmMensagem[]> => {
      if (CRM_DEMO_VISUAL) return getMockMensagens(conversaId!);
      const { data, error } = await db()
        .from("crm_mensagens")
        .select("*")
        .eq("conversa_id", conversaId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CrmMensagem[];
    },
  });

  useEffect(() => {
    if (!conversaId || CRM_DEMO_VISUAL) return;
    const channel = supabase
      .channel(`crm_mensagens_${conversaId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "crm_mensagens",
          filter: `conversa_id=eq.${conversaId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["crm", "mensagens", conversaId] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversaId, queryClient]);

  return query;
}

// ---------------------------------------------------------------------------
// Enviar mensagem (operador) — grava no banco e dispara webhook do n8n
// ---------------------------------------------------------------------------
export function useEnviarMensagem() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: {
      conversa: CrmConversa;
      corpo: string;
    }) => {
      const { conversa, corpo } = params;

      if (CRM_DEMO_VISUAL) {
        const msg: CrmMensagem = {
          id: `demo-msg-${Date.now()}`,
          conversa_id: conversa.id,
          canal_id: conversa.canal_id,
          direcao: "out",
          corpo,
          autor: "operador",
          evolution_message_id: null,
          status_envio: "enviado",
          enviada_por: user?.id ?? "demo-user",
          created_at: new Date().toISOString(),
        };
        const c = MOCK_CONVERSAS.find((x) => x.id === conversa.id);
        if (c) {
          c.ultima_msg_preview = corpo.slice(0, 120);
          c.ultima_msg_em = msg.created_at;
          c.nao_lidas = 0;
        }
        const bucket = MOCK_MENSAGENS;
        if (!bucket[conversa.id]) bucket[conversa.id] = getMockMensagens(conversa.id);
        bucket[conversa.id] = [...bucket[conversa.id], msg];
        return msg;
      }

      const { data: msg, error } = await db()
        .from("crm_mensagens")
        .insert({
          conversa_id: conversa.id,
          canal_id: conversa.canal_id,
          direcao: "out",
          corpo,
          autor: "operador",
          status_envio: "processando",
          enviada_por: user?.id ?? null,
        })
        .select()
        .single();
      if (error) throw error;

      await db()
        .from("crm_conversas")
        .update({
          ultima_msg_preview: corpo.slice(0, 120),
          ultima_msg_em: new Date().toISOString(),
        })
        .eq("id", conversa.id);

      // Dispara o envio real via n8n -> Evolution API
      if (crmConfig.webhookEnviarMensagem) {
        try {
          const resp = await fetch(crmConfig.webhookEnviarMensagem, {
            method: "POST",
            headers: crmWebhookHeaders(),
            body: JSON.stringify({
              conversa_id: conversa.id,
              mensagem_id: msg.id,
              canal_id: conversa.canal_id,
              telefone: conversa.telefone_cliente,
              corpo,
            }),
          });
          if (!resp.ok) throw new Error(`Webhook retornou ${resp.status}`);
        } catch (e) {
          await db()
            .from("crm_mensagens")
            .update({ status_envio: "erro" })
            .eq("id", msg.id);
          throw e;
        }
      }

      return msg as CrmMensagem;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["crm", "mensagens", vars.conversa.id] });
      queryClient.invalidateQueries({ queryKey: ["crm", "conversas"] });
    },
    onError: () => {
      toast.error("Não foi possível enviar a mensagem. Verifique a integração com o n8n.");
    },
  });
}

// ---------------------------------------------------------------------------
// Assumir conversa (handoff: operador humano assume, IA pausa)
// ---------------------------------------------------------------------------
export function useAssumirConversa() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (conversaId: string) => {
      if (CRM_DEMO_VISUAL) {
        const c = MOCK_CONVERSAS.find((x) => x.id === conversaId);
        if (c) {
          c.ia_ativa = false;
          c.responsavel_id = user?.id ?? "demo-user";
          c.estagio = "humano_atendendo";
        }
        return true;
      }
      const { error } = await db()
        .from("crm_conversas")
        .update({
          ia_ativa: false,
          responsavel_id: user?.id ?? null,
          estagio: "humano_atendendo",
        })
        .eq("id", conversaId);
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm", "conversas"] });
      toast.success("Você assumiu o atendimento. A IA foi pausada nesta conversa.");
    },
    onError: () => toast.error("Erro ao assumir a conversa."),
  });
}

// Devolver para a IA
export function useDevolverParaIA() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversaId: string) => {
      if (CRM_DEMO_VISUAL) {
        const c = MOCK_CONVERSAS.find((x) => x.id === conversaId);
        if (c) {
          c.ia_ativa = true;
          c.estagio = "ia_atendendo";
        }
        return true;
      }
      const { error } = await db()
        .from("crm_conversas")
        .update({ ia_ativa: true, estagio: "ia_atendendo" })
        .eq("id", conversaId);
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm", "conversas"] });
      toast.success("Atendimento devolvido para a IA.");
    },
    onError: () => toast.error("Erro ao devolver para a IA."),
  });
}

export function useMarcarComoLida() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversaId: string) => {
      if (CRM_DEMO_VISUAL) {
        const c = MOCK_CONVERSAS.find((x) => x.id === conversaId);
        if (c) c.nao_lidas = 0;
        return true;
      }
      const { error } = await db()
        .from("crm_conversas")
        .update({ nao_lidas: 0 })
        .eq("id", conversaId);
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm", "conversas"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Atualizar estágio (Kanban drag-and-drop)
// ---------------------------------------------------------------------------
export function useAtualizarEstagio() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (params: { conversaId: string; estagio: CrmEstagio }) => {
      if (CRM_DEMO_VISUAL) {
        const c = MOCK_CONVERSAS.find((x) => x.id === params.conversaId);
        if (c) {
          c.estagio = params.estagio;
          if (params.estagio === "humano_atendendo") {
            c.ia_ativa = false;
            c.responsavel_id = user?.id ?? "demo-user";
          } else if (params.estagio === "ia_atendendo") {
            c.ia_ativa = true;
          }
        }
        return true;
      }
      const patch: Record<string, unknown> = { estagio: params.estagio };
      if (params.estagio === "humano_atendendo") {
        patch.ia_ativa = false;
        patch.responsavel_id = user?.id ?? null;
      } else if (params.estagio === "ia_atendendo") {
        patch.ia_ativa = true;
      }
      const { error } = await db()
        .from("crm_conversas")
        .update(patch)
        .eq("id", params.conversaId);
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm", "conversas"] });
    },
    onError: () => toast.error("Erro ao mover o card."),
  });
}

// ---------------------------------------------------------------------------
// Campanhas
// ---------------------------------------------------------------------------
export function useCrmCampanhas() {
  return useQuery({
    queryKey: ["crm", "campanhas", CRM_DEMO_VISUAL ? "demo" : "live"],
    queryFn: async (): Promise<CrmCampanha[]> => {
      if (CRM_DEMO_VISUAL) return MOCK_CAMPANHAS;
      const { data, error } = await db()
        .from("crm_campanhas")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as CrmCampanha[];
    },
  });
}

// Pré-visualização do público-alvo de uma campanha (consulta a base de clientes)
export function useSegmentoClientes(filtros?: CrmCampanhaFiltros | null, enabled = true) {
  return useQuery({
    queryKey: ["crm", "segmento", filtros ?? null],
    enabled,
    queryFn: async () => {
      let q = supabase
        .from("clientes")
        .select("id, nome, telefone, cidade, estado", { count: "exact" })
        .not("telefone", "is", null);

      if (filtros?.plano_id) {
        const { data: orcs, error: orcErr } = await supabase
          .from("orcamentos")
          .select("cliente_id")
          .eq("plano_id", filtros.plano_id)
          .not("cliente_id", "is", null);
        if (orcErr) throw orcErr;
        const ids = [...new Set((orcs ?? []).map((o) => o.cliente_id))];
        if (ids.length === 0) return { clientes: [], total: 0 };
        q = q.in("id", ids);
      }
      if (filtros?.estado) q = q.eq("estado", filtros.estado);
      if (filtros?.cidade) q = q.ilike("cidade", `%${filtros.cidade}%`);

      const { data, error, count } = await q.limit(500);
      if (error) throw error;
      return { clientes: data ?? [], total: count ?? 0 };
    },
  });
}

export function useCriarCampanha() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (params: {
      nome: string;
      descricao?: string;
      tipo: string;
      mensagem_texto: string;
      canal_id?: string | null;
      filtros?: CrmCampanhaFiltros | null;
      total_destinatarios?: number;
    }) => {
      const { data, error } = await db()
        .from("crm_campanhas")
        .insert({
          nome: params.nome,
          descricao: params.descricao ?? null,
          tipo: params.tipo,
          mensagem_texto: params.mensagem_texto,
          canal_id: params.canal_id ?? null,
          filtros: params.filtros ?? null,
          total_destinatarios: params.total_destinatarios ?? 0,
          status: "rascunho",
          criado_por: user?.id ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as CrmCampanha;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm", "campanhas"] });
      toast.success("Campanha criada como rascunho.");
    },
    onError: () => toast.error("Erro ao criar campanha."),
  });
}

export function useCrmCampanhaEnvios(campanhaId?: string | null) {
  return useQuery({
    queryKey: ["crm", "campanha-envios", campanhaId],
    enabled: !!campanhaId,
    queryFn: async (): Promise<CrmCampanhaEnvio[]> => {
      const { data, error } = await db()
        .from("crm_campanha_envios")
        .select("*")
        .eq("campanha_id", campanhaId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as CrmCampanhaEnvio[];
    },
  });
}

export function useCancelarCampanha() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (campanhaId: string) => {
      const { error } = await db()
        .from("crm_campanhas")
        .update({ status: "cancelada" })
        .eq("id", campanhaId)
        .in("status", ["rascunho", "agendada", "em_andamento"]);
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm", "campanhas"] });
      toast.success("Campanha cancelada.");
    },
    onError: () => toast.error("Não foi possível cancelar a campanha."),
  });
}

export function useDispararCampanha() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (campanha: CrmCampanha) => {
      if (campanha.status === "em_andamento" || campanha.status === "concluida") {
        throw new Error("Esta campanha já foi disparada.");
      }
      if (campanha.status === "cancelada") {
        throw new Error("Não é possível disparar uma campanha cancelada.");
      }
      if (!crmConfig.webhookCampanha) {
        throw new Error("Webhook de campanha não configurado (VITE_N8N_WEBHOOK_CAMPANHA).");
      }
      await db()
        .from("crm_campanhas")
        .update({ status: "em_andamento" })
        .eq("id", campanha.id);

      const resp = await fetch(crmConfig.webhookCampanha, {
        method: "POST",
        headers: crmWebhookHeaders(),
        body: JSON.stringify({
          campanha_id: campanha.id,
          canal_id: campanha.canal_id,
          mensagem_texto: campanha.mensagem_texto,
          filtros: campanha.filtros,
        }),
      });
      if (!resp.ok) {
        await db()
          .from("crm_campanhas")
          .update({ status: "rascunho" })
          .eq("id", campanha.id);
        throw new Error(`Webhook retornou ${resp.status}`);
      }
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm", "campanhas"] });
      toast.success("Campanha enviada para processamento no n8n.");
    },
    onError: (e: Error) => toast.error(e.message || "Erro ao disparar campanha."),
  });
}
